import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { createOrder } from '../utils/api';
import { ArrowLeft, Send } from 'lucide-react';
import { toast } from 'sonner';

const OrderForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [formData, setFormData] = useState({
    contact_method: 'telegram',
    contact_value: '',
    uid: '',
    in_game_name: '',
    pop_needed_k: '',
    rate_per_k: '',
    start_time: '',
    upi_ref: '',
    payment_screenshot: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPG, PNG, WEBP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingFile(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, payment_screenshot: reader.result });
        toast.success('Screenshot uploaded successfully');
        setUploadingFile(false);
      };
      reader.onerror = () => {
        toast.error('Failed to upload file');
        setUploadingFile(false);
      };
      reader.readAsDataURL(file);
    } catch {
      toast.error('Failed to upload file');
      setUploadingFile(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Clean username: remove @ symbol and convert to lowercase
      let contactValue = formData.contact_value;
      if (formData.contact_method === 'telegram') {
        contactValue = contactValue.replace(/^@+/, '').toLowerCase();
      }

      const orderData = {
        ...formData,
        contact_value: contactValue,
        pop_needed_k: parseFloat(formData.pop_needed_k),
        rate_per_k: parseFloat(formData.rate_per_k)
      };

      const response = await createOrder(orderData);
      toast.success(`Order created successfully! Order ID: ${response.data.order_id}`);
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1219] relative overflow-hidden">
      <div className="scanline-overlay" />

      {/* Glow Effect */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      {/* Navbar */}
      <nav className="relative z-10 border-b border-primary/20">
        <div className="max-w-4xl mx-auto px-6 py-6 flex justify-between items-center">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ x: -4 }}
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-primary hover:text-white transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft />
            <span className="font-heading tracking-wider uppercase">Back</span>
          </motion.button>
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-primary" />
            <h1 className="text-2xl font-heading font-bold tracking-wider text-primary">GRINDNATION</h1>
          </div>
        </div>
      </nav>

      {/* Form Section */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-5xl font-heading font-bold text-white mb-4 tracking-wider">CREATE ORDER</h1>
          <p className="text-gray-400">Fill in the details below to place your popularity order</p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onSubmit={handleSubmit}
          className="bg-card border border-border/50 p-8"
          data-testid="order-form"
        >
          {/* Contact Method */}
          <fieldset className="mb-8">
            <legend className="block text-primary font-heading tracking-wider uppercase mb-4 text-sm">Contact Method</legend>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, contact_method: 'telegram' })}
                className={`p-4 border ${
                  formData.contact_method === 'telegram'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-gray-400 hover:border-primary/50'
                } font-heading tracking-wider uppercase transition-colors`}
                data-testid="contact-method-telegram"
              >
                Telegram
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, contact_method: 'email' })}
                className={`p-4 border ${
                  formData.contact_method === 'email'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-gray-400 hover:border-primary/50'
                } font-heading tracking-wider uppercase transition-colors`}
                data-testid="contact-method-email"
              >
                Email
              </button>
            </div>
          </fieldset>

          {/* Contact Value */}
          <div className="mb-6">
            <label htmlFor="contact-value" className="block text-primary font-heading tracking-wider uppercase mb-2 text-sm">
              {formData.contact_method === 'telegram' ? 'Telegram Username' : 'Email Address'}
            </label>
            <input
              id="contact-value"
              type="text"
              name="contact_value"
              value={formData.contact_value}
              onChange={handleChange}
              required
              placeholder={formData.contact_method === 'telegram' ? 'Username (without @)' : 'your@email.com'}
              className="w-full bg-black/50 border-b-2 border-primary/30 focus:border-primary focus:outline-none px-4 py-3 font-mono text-white placeholder-gray-600"
              data-testid="contact-value-input"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* UID */}
            <div>
              <label htmlFor="uid" className="block text-primary font-heading tracking-wider uppercase mb-2 text-sm">UID</label>
              <input
                id="uid"
                type="text"
                name="uid"
                value={formData.uid}
                onChange={handleChange}
                required
                placeholder="123456789"
                className="w-full bg-black/50 border-b-2 border-primary/30 focus:border-primary focus:outline-none px-4 py-3 font-mono text-white placeholder-gray-600"
                data-testid="uid-input"
              />
            </div>

            {/* In-Game Name */}
            <div>
              <label htmlFor="in-game-name" className="block text-primary font-heading tracking-wider uppercase mb-2 text-sm">In-Game Name</label>
              <input
                id="in-game-name"
                type="text"
                name="in_game_name"
                value={formData.in_game_name}
                onChange={handleChange}
                required
                placeholder="PlayerName"
                className="w-full bg-black/50 border-b-2 border-primary/30 focus:border-primary focus:outline-none px-4 py-3 font-mono text-white placeholder-gray-600"
                data-testid="in-game-name-input"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Pop Needed */}
            <div>
              <label htmlFor="pop-needed" className="block text-primary font-heading tracking-wider uppercase mb-2 text-sm">Pop Needed (K)</label>
              <input
                id="pop-needed"
                type="number"
                step="0.1"
                name="pop_needed_k"
                value={formData.pop_needed_k}
                onChange={handleChange}
                required
                placeholder="10"
                className="w-full bg-black/50 border-b-2 border-primary/30 focus:border-primary focus:outline-none px-4 py-3 font-mono text-white placeholder-gray-600"
                data-testid="pop-needed-input"
              />
            </div>

            {/* Rate per K */}
            <div>
              <label htmlFor="rate-per-k" className="block text-primary font-heading tracking-wider uppercase mb-2 text-sm">Rate per K (₹)</label>
              <input
                id="rate-per-k"
                type="number"
                step="0.01"
                name="rate_per_k"
                value={formData.rate_per_k}
                onChange={handleChange}
                required
                placeholder="100"
                className="w-full bg-black/50 border-b-2 border-primary/30 focus:border-primary focus:outline-none px-4 py-3 font-mono text-white placeholder-gray-600"
                data-testid="rate-per-k-input"
              />
            </div>
          </div>

          {/* Total Price Display */}
          {formData.pop_needed_k && formData.rate_per_k && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-6 p-4 bg-black/40 border border-primary/20"
              data-testid="total-price-display"
            >
              <div className="flex justify-between items-center">
                <span className="text-gray-400 font-mono uppercase tracking-wider text-sm">Total Price:</span>
                <span className="text-3xl font-mono font-bold text-primary">
                  ₹{(parseFloat(formData.pop_needed_k) * parseFloat(formData.rate_per_k)).toFixed(2)}
                </span>
              </div>
            </motion.div>
          )}

          {/* Start Time */}
          <div className="mb-6">
            <label htmlFor="start-time" className="block text-primary font-heading tracking-wider uppercase mb-2 text-sm">Start Time</label>
            <input
              id="start-time"
              type="text"
              name="start_time"
              value={formData.start_time}
              onChange={handleChange}
              required
              placeholder="e.g., 8:00 PM IST"
              className="w-full bg-black/50 border-b-2 border-primary/30 focus:border-primary focus:outline-none px-4 py-3 font-mono text-white placeholder-gray-600"
              data-testid="start-time-input"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* UPI Reference (Optional) */}
            <div>
              <label htmlFor="upi-ref" className="block text-gray-400 font-heading tracking-wider uppercase mb-2 text-sm">UPI Ref (Optional)</label>
              <input
                id="upi-ref"
                type="text"
                name="upi_ref"
                value={formData.upi_ref}
                onChange={handleChange}
                placeholder="Transaction ID"
                className="w-full bg-black/50 border-b-2 border-muted/30 focus:border-primary focus:outline-none px-4 py-3 font-mono text-white placeholder-gray-600"
                data-testid="upi-ref-input"
              />
            </div>

            {/* Payment Screenshot (Optional) */}
            <div>
              <label htmlFor="payment-screenshot" className="block text-gray-400 font-heading tracking-wider uppercase mb-2 text-sm">
                Payment Screenshot (Optional)
              </label>
              <div className="relative">
                <input
                  id="payment-screenshot"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleFileUpload}
                  disabled={uploadingFile}
                  className="w-full bg-black/50 border-b-2 border-muted/30 focus:border-primary focus:outline-none px-4 py-3 font-mono text-white file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-primary/20 file:text-primary file:font-mono file:text-sm file:font-bold file:uppercase file:tracking-wider hover:file:bg-primary/30 file:cursor-pointer disabled:opacity-50"
                  data-testid="payment-screenshot-input"
                />
                {uploadingFile && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {formData.payment_screenshot && !uploadingFile && (
                  <div className="mt-2 flex items-center gap-2 text-primary text-sm font-mono">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Screenshot uploaded
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full group relative px-8 py-4 bg-primary text-black font-heading font-bold text-lg tracking-widest uppercase overflow-hidden disabled:opacity-50"
            style={{ clipPath: 'polygon(0 0, 100% 0, 100% 70%, 95% 100%, 0 100%)' }}
            data-testid="submit-order-button"
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              {loading ? 'PROCESSING...' : 'SUBMIT ORDER'}
              {!loading && <Send className="group-hover:translate-x-1 transition-transform" size={20} />}
            </span>
            <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity" />
          </motion.button>
        </motion.form>
      </div>
    </div>
  );
};

export default OrderForm;